import { createRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { isKeyboardEventType } from '../types/event';
import { wrapOutOfBoundIndex } from '../utils/array';

interface UseKeyboardListControlConfig<T = unknown> {
	emitClickOnSelect?: boolean;
	focusSelected?: boolean;
	onEscapeKeyDown?: VoidFunction;
	onSelectOption?: (option: T) => void;
	options: T[];
	selectOnTab?: boolean;
}

enum Key {
	Down = 'ArrowDown',
	Enter = 'Enter',
	Escape = 'Escape',
	Tab = 'Tab',
	Up = 'ArrowUp',
}

interface Interactable {
	click: VoidFunction;
	focus: VoidFunction;
}

export const useKeyboardListControl = <T = unknown, U = HTMLElement>({
	emitClickOnSelect = false,
	focusSelected = false,
	onEscapeKeyDown,
	onSelectOption,
	options,
	selectOnTab = true,
}: UseKeyboardListControlConfig<T>) => {
	const [selectedOption, setSelectedOption] = useState(options[0]);

	const refMap = useMemo(() => new Map(options.map((option) => [option, createRef<U>()])), [options]);

	useLayoutEffect(() => {
		setSelectedOption(options[0]);
	}, [options]);

	const isSelected = (option: T) => option === selectedOption;

	const register = (option: T) => ({
		onClick: () => onSelectOption?.(option),
		onMouseOver: () => setSelectedOption(option),
		...((emitClickOnSelect || focusSelected) && { ref: refMap.get(option) }),
		onMouseOut: () => setSelectedOption(options[-1]),
	});

	useEffect(() => {
		const onPressKey = (e: Event) => {
			/* istanbul ignore if - Unable to test, function is only a typeguard to work with diff between React and DOM types */
			if (!isKeyboardEventType(e)) {
				return;
			}

			const key = e.key as Key;

			if (!Object.values(Key).includes(key)) {
				return;
			}

			if (e.cancelable) {
				e.preventDefault();
			}

			if (key === Key.Escape && onEscapeKeyDown) {
				onEscapeKeyDown();
				return;
			}

			if (key === Key.Enter || (key === Key.Tab && selectOnTab)) {
				onSelectOption?.(selectedOption);

				if (emitClickOnSelect) {
					(refMap.get(selectedOption)?.current as unknown as Interactable)?.click?.();
				}

				return;
			}

			const newOption = options[wrapOutOfBoundIndex(options.indexOf(selectedOption) + (key === Key.Down || key === Key.Tab ? 1 : -1), options.length)];

			if (focusSelected) {
				(refMap.get(newOption)?.current as unknown as Interactable)?.focus?.();
			}

			setSelectedOption(newOption);
		};

		window.addEventListener('keydown', onPressKey);

		return () => window.removeEventListener('keydown', onPressKey);
	}, [emitClickOnSelect, focusSelected, onEscapeKeyDown, onSelectOption, options, refMap, selectOnTab, selectedOption]);

	return {
		isSelected,
		register,
	};
};
